import { create } from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import { type AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { GapRepresentativeValue } from '@ebalance/proto/bffuser/v1/supply_demand_gap_model_pb'
import { getSupplyDemandGap } from '@ebalance/proto/bffuser/v1/supply_demand_gap_service-SupplyDemandGapService_connectquery'
import {
  GetSupplyDemandGapRequestSchema,
  type GetSupplyDemandGapResponse,
} from '@ebalance/proto/bffuser/v1/supply_demand_gap_service_pb'
import {
  DateSchema,
  type Date as ProtoDate,
} from '@ebalance/proto/google/type/date_pb'
import { type UseQueryResult, keepPreviousData } from '@tanstack/react-query'

import { startOfTodayJst } from '~/models/dateTime'
import type { GapFilterState } from '~/pages/SupplyGap/models/types'
import { parseYmd, toProtoRangeType } from '~/pages/SupplyGap/models/types'

/** フィルタ state から period_from / period_to を計算する */
function computePeriod(filter: GapFilterState): {
  from: Date
  to: Date
} {
  // 保存値が壊れていた時のフォールバック基点。
  const now = startOfTodayJst()

  switch (filter.rangeType) {
    case 'day': {
      const d = parseYmd(filter.dayDate) ?? now
      return { from: d, to: d }
    }
    case 'week': {
      const start = parseYmd(filter.weekStart) ?? now
      const end = new Date(start)
      end.setDate(start.getDate() + filter.weekDur * 7 - 1)
      return { from: start, to: end }
    }
    case 'month': {
      const start = parseYmd(filter.monthStart) ?? now
      const end = new Date(
        start.getFullYear(),
        start.getMonth() + filter.monthDur,
        0,
      )
      return { from: start, to: end }
    }
  }
}

function toProtoDateObj(d: Date): ProtoDate {
  return create(DateSchema, {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
  })
}

/**
 * AC-SDG-011 の画面内エラー導線を出し分けるための取得失敗 2 状態。 どちらも false なら
 * 「失敗していない」 (= ロード中・成功中)。
 */
export type FetchErrorState = {
  /** まだ一度も取得に成功しておらず失敗している (= 表示できるデータが無い)。 */
  hasFailedWithoutData: boolean
  /** 一度成功した後の再取得に失敗している (= 古いデータを表示し続けている)。 */
  hasFailedAfterSuccess: boolean
}

/**
 * React Query の result から取得失敗の 2 状態を導出する。
 *
 * React Query は「まだ一度も成功していない query」の再取得開始時に status を
 * 'error' → 'pending' へ、 error を null へリセットするため、 `isError` だけでは
 * 再読み込み中に判定が false へ落ちて Loading 表示に切り替わってしまう。
 * `errorUpdatedAt` には過去の失敗タイムスタンプが残るので、 再取得中も
 * 「過去に失敗した事実」 を検出できる (= 初回失敗の latch)。
 *
 * 逆に data を持つ query は再取得中も status が 'error' のまま保たれ、 成功すると
 * false へ戻るので、 成功後の失敗は `isError` で判定する (`errorUpdatedAt` は成功後も
 * 残るため latch には使えない)。
 */
export function resolveFetchErrorState(
  query: Pick<
    UseQueryResult<unknown, ConnectError>,
    'data' | 'errorUpdatedAt' | 'isError'
  >,
): FetchErrorState {
  const hasData = query.data !== undefined
  return {
    hasFailedWithoutData: !hasData && query.errorUpdatedAt > 0,
    hasFailedAfterSuccess: hasData && query.isError,
  }
}

/**
 * global QueryClient の `throwOnError: true` を本 query だけ `false` に上書きする。
 * 取得失敗は AC-SDG-011 のとおり呼び出し元がチャート描画エリア内の Alert +
 * 再読み込み導線で扱うため、ErrorLayout (全画面エラー) に飛ばさない。
 * 注意: テスト用 QueryClient (testUtils) は throwOnError: false を強制するため、
 * この上書きの欠落は既定の F5 テストでは検出できない (プロダクトのみで全画面
 * エラー化する)。`queryThrowOnError: true` を付けた F5 テストが回帰を押さえる。
 *
 * 上記の `throwOnError` 上書きと対で、 呼び出し元が出し分ける取得失敗の 2 状態
 * (`FetchErrorState`) も本 hook が名前付きで返す (= React Query の state 遷移の知識を
 * page 側に漏らさない)。
 */
export function useGetSupplyDemandGap(
  filter: GapFilterState,
  selectedAreaCodes: AreaCode[],
  options?: {
    /**
     * false の間は RPC を発火しない (issue #7131 / AC-WFW-006a Decision 1:
     * ワークフローケース対象日が確定するまで対象日依存のデータ取得を開始しない)。
     * 省略時は従来どおり常時有効 (= 後方互換)。
     */
    enabled?: boolean
  },
): UseQueryResult<GetSupplyDemandGapResponse, ConnectError> & FetchErrorState {
  const { from, to } = computePeriod(filter)

  const query = useQuery(
    getSupplyDemandGap,
    create(GetSupplyDemandGapRequestSchema, {
      rangeType: toProtoRangeType(filter.rangeType),
      periodFrom: toProtoDateObj(from),
      periodTo: toProtoDateObj(to),
      areaCodes: selectedAreaCodes,
      representativeValue:
        filter.rangeType === 'day'
          ? GapRepresentativeValue.BASE
          : filter.representativeValue,
    }),
    {
      enabled: options?.enabled,
      // 条件 (期間・エリア・代表値) を変えた直後は、 次の結果が届くまで前の条件の結果を
      // 残す (`isPlaceholderData`)。 画面はそれを影の下に置き、 領域を空にしない
      // (UI 辞書 region-loading.md「条件を変えた時に領域を空にしない」)。 取得に失敗した
      // 時は placeholder は外れる (data は undefined) ので、 失敗の 2 状態の判定
      // (`resolveFetchErrorState`) は変わらない。
      placeholderData: keepPreviousData,
      // 需給ギャップは参照系で自動更新不要
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      throwOnError: false,
    },
  )

  return { ...query, ...resolveFetchErrorState(query) }
}
